import Vue from "vue";
import Component from 'vue-class-component';
import { Esri } from '@/esriMap';
import { IModalBase, DataItem, modalHelper } from '@/components';
import { Deferred } from '@/utils/Deferred';
import { Prop, Watch } from 'vue-property-decorator';
import { arcgisToGeoJSON, geojsonToArcGIS } from "@esri/arcgis-to-geojson-utils";
import { symbol, parentSymbol, invalidSymbol } from '../module';
import { areaSpecieProvenienzaStore } from '@/modules/AreaSpecieProvenienza/store/areaSpecieProvenienza.store';
import { addFeatureLayers, addMapImageLayer, addExistingGraphics } from '@/utils/mapFeatureLayer';
import { piantaSemeMarzeStore } from '../store/PiantaSemeMarze.store';
import { toastHelper } from '@/helpers/ToastHelper';
import { layoutHelper } from '@/helpers/LayoutHelper';
import { deviceHelper } from "@/helpers/DeviceHelper";
import { onlineCheck } from "@/utils/onlineCheck";
@Component({})
export default class Map extends Vue implements IModalBase<server.PiantaSemeMarze>{
  @Prop()
  value: server.PiantaSemeMarze;

  @Prop()
  deferred: Deferred<DataItem<server.PiantaSemeMarze>>;

  map: __esri.Map = null;
  mapview: __esri.MapView = null;

  layer: __esri.GraphicsLayer = null;

  currentGraphic: __esri.Graphic;
  isValidGeometry: boolean = false;

  parentGraphic: __esri.Graphic = null;
  parentLayer: __esri.GraphicsLayer = null;
  exsitingGraphics: __esri.Graphic[] = [];

  enableCapture: boolean = false;

  geoUtils: __esri.webMercatorUtils;
  geoEngine: __esri.geometryEngine;
  isOnline: boolean;

  async created() {
    this.isOnline = await onlineCheck();
  }

  async mounted() {
    this.geoUtils = await Esri.Geometry.Support.webMercatorUtils();
    this.geoEngine = await Esri.Geometry.geometryEngine();

    this.map = await Esri.Map({ basemap: 'hybrid' } as __esri.MapProperties);
    this.mapview = await Esri.Views.MapView({
      map: this.map,
      zoom: 12,
      center: [12, 43],
      container: "map",
      spatialReference: {
        wkid: 102100
      },
      popup: {
        defaultPopupTemplateEnabled: true,
        collapseEnabled: true,
        collapsed: false,
      }
    } as __esri.MapViewProperties);

    this.mapview.focus();

    this.parentLayer = await Esri.Layers.GraphicsLayer({
      listMode: 'hide'
    });
    this.layer = await Esri.Layers.GraphicsLayer({
      listMode: 'hide'
    });
    if (this.isOnline)
      this.map.layers.add(this.parentLayer, 1);

    this.map.layers.add(this.layer, 2);

    let parent = await areaSpecieProvenienzaStore.getters.get(this.value.areaSpecieProvenienzaId);
    if (parent && parent.geoJSON) this.parentGraphic = await this.addParentGraphic(parent.geoJSON);

    if (this.value.geoJSON)
      this.currentGraphic = await this.addGraphicFromGeoJSON(this.value.geoJSON, symbol, this.layer, true)

    if (this.isOnline) {
      await this.addTools();
      await addMapImageLayer(this.map);
      await addFeatureLayers(this.map);
      const points = (await piantaSemeMarzeStore.getters.getAll()).filter(f => f.id !== this.value.id && f.areaSpecieProvenienzaId === f.areaSpecieProvenienzaId);
      this.exsitingGraphics = await addExistingGraphics(this.map, "point", "Piante da seme/da marze presenti", points.map(m => { return { nome: m.codice, geoJSON: m.geoJSON } }));
    }

    const $this = this;
    this.mapview.on("click", async (event) => {
      console.info(`capture point enable: ${this.enableCapture}`, event);
      if (!this.enableCapture || !event?.mapPoint)
        return;

      this.layer.removeAll();
      this.isValidGeometry = false;

      let point = await Esri.Geometry.Point({ longitude: event.mapPoint.longitude, latitude: event.mapPoint.latitude } as __esri.PointProperties);
      if (point && point.spatialReference.isWGS84)
        point = $this.geoUtils.geographicToWebMercator(point) as __esri.Point;

      $this.currentGraphic = await $this.addGraphic(point, symbol, $this.layer, true, this.isValidGeometry);
      this.enableCapture = false;
    });
  }

  async addParentGraphic(geojson: __esri.Geometry) {
    let polygonConstructor: __esri.PolygonConstructor = await Esri.Geometry.PolygonConstructor();
    let polygonGeometry = new polygonConstructor(geojsonToArcGIS(geojson));
    polygonGeometry = (await Esri.Geometry.Support.webMercatorUtils()).geographicToWebMercator(polygonGeometry) as any;

    let g = await Esri.Graphic({
      geometry: polygonGeometry,
      symbol: parentSymbol
    });
    this.parentLayer.graphics.add(g)
    await this.mapview.goTo(polygonGeometry)
    return g;
  }

  async addGraphicFromGeoJSON(geojson: any, symbol: any, layer: __esri.GraphicsLayer, isPrincipal: boolean = false, isValidGeometry: boolean = false): Promise<__esri.Graphic> {
    const geoUtils = await Esri.Geometry.Support.webMercatorUtils();
    let pointConstructor: __esri.PointConstructor = await Esri.Geometry.PointConstructor();
    let geometry = new pointConstructor(geojsonToArcGIS(geojson));

    geometry = geoUtils.geographicToWebMercator(geometry) as any;
    return await this.addGraphic(geometry, symbol, layer, isPrincipal, isValidGeometry);
  }


  async addGraphic(geometry: __esri.Point, _symbol: any, layer: __esri.GraphicsLayer, isPrincipal: boolean, isValidGeometry: boolean) {
    let graphic = await Esri.Graphic({
      geometry: geometry,
      symbol: _symbol
    });

    if (isPrincipal) {
      this.checkGraphic(graphic, true);
      await this.mapview.goTo(geometry)
    }

    layer.add(graphic);
    return graphic;
  }

  async addTools() {
    var searchWidget = await Esri.Widgets.Search({ view: this.mapview })
    var expand = await Esri.Widgets.Expand({ id: "search", expandIconClass: "esri-icon-search", expandTooltip: "Trova indirizzo o luogo", view: this.mapview, content: searchWidget });
    this.mapview.ui.add(expand, "top-left");

    Esri.WidgetsFactory.BasemapGallery(this.mapview, "top-left");
    Esri.WidgetsFactory.LayerList(this.mapview, "top-left");

    const map_buttons = document.getElementById("map-buttons");
    this.mapview.ui.add(map_buttons);

    const sampleInstructions = document.getElementById("instruction-widget");
    let instructionsExpand = await Esri.Widgets.Expand({
      id: "question",
      expandIconClass: "esri-icon-question",
      expandTooltip: "Come catturare la geometria",
      expanded: layoutHelper.isWeb,
      view: this.mapview,
      content: sampleInstructions
    });
    this.mapview.ui.add(instructionsExpand, "top-left");
  }

  async checkGraphic(graphic: any, showMessage: boolean = false) {
    this.isValidGeometry = false;

    const geoEngine = await Esri.Geometry.geometryEngine();
    let geometry = graphic.geometry;

    const checkNotContains = this.parentGraphic && !geoEngine.contains(this.parentGraphic.geometry, geometry);
    const checkIntersect = this.exsitingGraphics.length > 0 && (geoEngine.intersect(this.exsitingGraphics.map(m => m.geometry), geometry) as __esri.Geometry[]).filter(f => f != null).length > 0;

    if (checkNotContains) {
      graphic.symbol = invalidSymbol;
      if (showMessage) toastHelper.error("La pianta seme/da marze deve essere contenuta<br>nell'area specie provenienza selezionata");
    } else {
      if (checkIntersect) {
        graphic.symbol = invalidSymbol;
        if (showMessage) toastHelper.error("La pianta seme/da marze non si può sovrapporre<br>ad altre piante seme da marze inserite");
      } else {
        graphic.symbol = symbol
        this.currentGraphic = graphic;
        this.isValidGeometry = true;
      }
    }
  }


  close() {
    modalHelper.Unchanged(this);
    modalHelper.closeModal("extended");
  }

  async saveGeometry() {
    let geographicGeometry = (await Esri.Geometry.Support.webMercatorUtils()).webMercatorToGeographic(this.currentGraphic.geometry)
    this.value.geoJSON = arcgisToGeoJSON(geographicGeometry);
    modalHelper.Changed(this);
  }
}